import { useState, useEffect, useMemo } from "react";
import { getNivelAcessoFromUser } from "../frontend/js/auth.js";

/**
 * Mapeamento de permissões por acesso_autorizacao:
 * 0 = Empresa
 * 1 = Estudante
 * 2 = SEOPE
 * 3 = SECEX
 * 4 = INFOR
 */
const PERMISSOES = {
  0: {
    label: "Empresa",
    podeEditarEmpresa: true,
    podeEditarEstudante: false,
    podeEditarTudo: false,
    podeAprovar: false,
    podeReprovar: false,
    podeSubmeter: false,
    podeFinalizar: false,
    podeVerHistorico: true,
    podeAnexar: true,
    podeVerObservacoesCiee: false,
  },
  1: {
    label: "Estudante",
    podeEditarEmpresa: false,
    podeEditarEstudante: true,
    podeAprovar: false,
    podeReprovar: false,
    podeSubmeter: true,
    podeFinalizar: false,
    podeVerHistorico: true,
    podeAnexar: true,
    podeVerObservacoesCiee: false,
  },
  2: {
    label: "SEOPE",
    podeEditarEmpresa: true,
    podeEditarEstudante: true,
    podeEditarTudo: true,
    podeAprovar: true,
    podeReprovar: true,
    podeSubmeter: false,
    podeFinalizar: true,
    podeVerHistorico: true,
    podeAnexar: true,
    podeVerObservacoesCiee: true,
  },
  3: {
    label: "SECEX",
    podeEditarEmpresa: true,
    podeEditarEstudante: true,
    podeEditarTudo: true,
    podeAprovar: true,
    podeReprovar: true,
    podeSubmeter: false,
    podeFinalizar: true,
    podeVerHistorico: true,
    podeAnexar: false,
    podeVerObservacoesCiee: true,
  },
  4: {
    label: "INFOR",
    podeEditarEmpresa: true,
    podeEditarEstudante: true,
    podeEditarTudo: true,
    podeAprovar: true,
    podeReprovar: true,
    podeSubmeter: true,
    podeFinalizar: true,
    podeVerHistorico: true,
    podeAnexar: true,
    podeVerObservacoesCiee: true,
  },
};

const PERMISSAO_DEFAULT = PERMISSOES[0];

export function usePermissoes() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        setUser(JSON.parse(userStr));
      }
    } catch (err) {
      console.error("Erro ao ler user do localStorage:", err);
    }
  }, []);

  const permissao = useMemo(() => {
    if (!user) return PERMISSAO_DEFAULT;

    // Usa fallback amplo: tenta `acesso_autorizacao` e, na falta,
    // cai em outros claims do JWT (`tipo_acesso`, `nivel_acesso`,
    // `role_id`, `role`). Também reforça nível 3 quando
    // `tipo === "usuario_ciee"` para SECEX/INFOR/SEOPE.
    const acesso = getNivelAcessoFromUser(user);
    return PERMISSOES[acesso] || PERMISSAO_DEFAULT;
  }, [user]);

  const podeEditarCampo = (secao) => {
    if (permissao.podeEditarTudo) return true;

    if (
      secao === "empresa" ||
      secao === "supervisor" ||
      secao === "estagio" ||
      secao === "horarios" ||
      secao === "comprovante" ||
      secao === "observacoes"
    ) {
      return permissao.podeEditarEmpresa;
    }

    if (secao === "estudante" || secao === "escolares" || secao === "anexos") {
      return permissao.podeEditarEstudante;
    }

    return false;
  };

  return {
    user,
    permissao,
    acessoAutorizacao: user?.acesso_autorizacao ?? 0,
    tipoLabel: permissao.label,
    podeEditarCampo,
    isEmpresa: permissao.label === "Empresa",
    isEstudante: permissao.label === "Estudante",
    isCieemg: ["SEOPE", "SECEX", "INFOR"].includes(permissao.label),
  };
}

export default usePermissoes;
